import { IOffer } from '@clickster/constant-definitions/dist-esm/entities/offer/types';
import { IQueryProps } from '../interfaces/general/api';
import { OfferService } from '../services/offers';
import { useMutation, useQuery, useQueries } from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import { IOfferCategory } from '@clickster/constant-definitions/dist-esm/entities/offer-category/types';
import { useMemo } from 'react';
import { ISelectOption } from '../components/ui/shadcn/floating-label-select';
import { removeKeysFromObject } from '../utils/objects';
import { useGetAffiliatesNetwork } from './affiliates';

export const useGetOffers = ({ params, handleSuccess, enabled, customKey }: IQueryProps<IOffer> = {}) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery([customKey ? customKey : 'offers'], () => OfferService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });

  const { affiliates: affiliatesNetwork } = useGetAffiliatesNetwork({
    params: {
      select: '_id name',
    },
    keyName: '_id_name',
  });

  const offersCategories = useMemo(() => {
    const categories = uniqBy(data as IOffer[], 'category.name').map((item) => ({
      ...item.category,
      _id: item.category?._id,
    }));

    return categories.map((item) => ({
      _id: item._id || '',
      name: item.name || '',
    }));
  }, [data]);

  return {
    offers: orderBy(data ?? [], ['name'], ['asc']) as IOffer[],
    offersCategories,
    affiliatesNetwork: affiliatesNetwork?.map((affiliate) => ({
      _id: affiliate._id,
      name: affiliate.name || '',
    })),
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetOfferById extends IQueryProps<IOffer> {
  id: string;
}

export const useGetOfferById = ({ id, enabled, handleSuccess }: IUseGetOfferById) => {
  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['offerById', id], () => OfferService.find(id), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    enabled: enabled && !!id,
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

  return {
    offer: removeKeysFromObject((data ?? {}) as IOffer, ['__v', 'deleted']),
    isLoading,
    isFetching,
    refetch,
  };
};

export const useGetOfferCategories = ({ enabled, handleSuccess }: IQueryProps<IOfferCategory[]>) => {
  const {
    data = [] as IOfferCategory[],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['offerCategories'], () => OfferService.findCategories(), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: (data) => {
      if (handleSuccess) handleSuccess(data);
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const dataOrdered = useMemo(() => removeKeysFromObject(orderBy(data ?? [], ['name'], ['asc']), ['__v']), [data]);

  const offerCategoriesOptions: ISelectOption[] = useMemo(() => {
    const options: ISelectOption[] = dataOrdered?.map((category: IOfferCategory) => ({
      value: category?._id!,
      label: category?.name!,
      id: category?._id!,
    }));

    return options;
  }, [dataOrdered]);

  return {
    offerCategories: dataOrdered,
    offerCategoriesOptions,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useUpdateOfferById = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((offer: IOffer) => OfferService.update(offer), {
    onError: (error) => console.log({ error }),
  });

  return {
    updateOfferById: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useCreateOffer = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((offer: IOffer) => OfferService.create(offer), {
    onError: (error) => console.log({ error }),
  });

  return {
    createOffer: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useGetCountries = () => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery(['countries'], () => OfferService.findCountries(), {
    onError: (error) => {
      console.error('Error in useGetCountries:', error);
    },
    onSuccess: (data) => {
      // console.log('Countries fetched successfully:', data);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    countries: data as any[],
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

/**
 * Hook to fetch regions for a specific country
 * @param countryCode - The country code to fetch regions for
 * @returns Object containing regions data and loading state
 */
export const useGetRegionsByCountry = (countryCode: string) => {
  const {
    data: regions = [],
    isLoading,
    isFetching,
  } = useQuery(['regions', countryCode], () => OfferService.getRegionsByCountry(countryCode), {
    enabled: !!countryCode,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const formattedRegions = useMemo(() => {
    if (!Array.isArray(regions) || !regions.length) return [];

    return regions.map((region) => ({
      id: region.regionCode,
      name: region.regionName,
      value: region.regionCode,
    }));
  }, [regions]);

  return {
    regions: formattedRegions,
    isLoading,
    isFetching,
  };
};

/**
 * Hook to fetch regions for multiple countries in parallel
 * @param countryCodes - Array of country codes to fetch regions for
 * @returns Object containing regions data and loading states for all countries
 */
export const useGetRegionsByCountries = (countryCodes: string[]) => {
  const { countries: rawCountries = [] } = useGetCountries();

  const queries = useQueries({
    queries: countryCodes.map((code) => ({
      queryKey: ['regions', code],
      queryFn: () => OfferService.getRegionsByCountry(code),
      enabled: !!code,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });

  const formattedRegions = useMemo(() => {
    return queries.reduce<{ id: string; name: string; value: string; countryName: string }[]>((acc, query, index) => {
      if (!query.data || !Array.isArray(query.data)) return acc;

      const countryCode = countryCodes[index];
      const country = rawCountries.find((c) => c.code === countryCode);
      if (!country) return acc;

      const regions = query.data.map((region) => ({
        id: region.regionCode,
        name: `${region.regionName} - ${country.name}`,
        value: region.regionCode,
        countryName: country.name,
      }));

      return [...acc, ...regions];
    }, []);
  }, [queries, countryCodes, rawCountries]);

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);

  return {
    regions: formattedRegions,
    isLoading,
    isFetching,
  };
};

/**
 * Hook to fetch cities for multiple regions
 * @param selectedRegions - Array of selected regions with format "regionName - countryName"
 * @returns Object containing cities data and loading state
 */
export const useGetCitiesByRegion = (selectedRegions: Array<{ id: string; name: string }>) => {
  const queries = useQueries({
    queries: selectedRegions.map((region) => {
      // Extract region and country from the combined name
      const [regionName, countryName] = region.name.split(' - ');

      return {
        queryKey: ['cities', countryName, regionName],
        queryFn: () => OfferService.getCitiesByRegion(countryName, regionName),
        enabled: !!countryName && !!regionName,
        retry: false,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);

  const formattedCities = useMemo(() => {
    const allCities: { id: string; name: string; value: string }[] = [];

    selectedRegions.forEach((region, index) => {
      const query = queries[index];
      if (!query.data) return;

      const [regionName, countryName] = region.name.split(' - ');

      query.data.forEach((cityName) => {
        allCities.push({
          id: `${cityName} - ${regionName} - ${countryName}`,
          name: `${cityName} - ${regionName} - ${countryName}`,
          value: cityName,
        });
      });
    });

    return allCities;
  }, [queries, selectedRegions]);

  return {
    cities: formattedCities,
    isLoading,
    isFetching,
  };
};
